import { Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";

type StyleProps = {
  menuWidth: number;
  menuMaxHeight?: number;
};

export const useStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: {
    minWidth: "auto",
    width: "fit-content",
    backgroundColor: "transparent",
  },
  fieldRoot: {
    alignSelf: "flex-end",
  },
  select: {
    height: "32px",
    minHeight: "32px",
    width: "auto",
    borderRadius: "9999px",
    overflow: "hidden",
    backgroundColor: `${theme.palette.background.paper} !important`,
    color: theme.palette.primary.main,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
      borderRadius: "9999px",
    },
    "&:hover": {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused": {
      backgroundColor: `${theme.palette.background.paper} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: "32px !important",
      minHeight: "32px !important",
      boxSizing: "border-box",
      padding: "6px 12px !important",
      backgroundColor: "transparent !important",
    },
  },
  selectValue: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    width: "100%",
    height: "32px",
    direction: "rtl",
    color: theme.palette.primary.main,
  },
  filterIcon: {
    width: "20px",
    height: "20px",
    flexShrink: 0,
  },
  selectText: {
    flex: 1,
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    fontSize: "14px",
    lineHeight: "140%",
    whiteSpace: "nowrap",
    textAlign: "right",
  },
  selectChevron: {
    fontSize: "20px !important",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  menu: {
    direction: "rtl",
    marginTop: 4,
    boxSizing: "border-box",
    paddingTop: 4,
    paddingBottom: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    opacity: 1,
    boxShadow: "0 4px 16px rgba(37, 56, 88, 0.12)",
    overflow: "hidden",
    maxHeight: ({ menuMaxHeight }) =>
      menuMaxHeight ? `${menuMaxHeight}px` : "none",
  },
  menuList: {
    paddingTop: 0,
    paddingBottom: 0,
    overflowY: "auto",
    maxHeight: ({ menuMaxHeight }) =>
      menuMaxHeight ? `${menuMaxHeight - 8}px` : "none",
  },
  menuItem: {
    height: 52,
    minHeight: "52px !important",
    boxSizing: "border-box",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "8px !important",
    paddingRight: "16px !important",
    opacity: 1,
    transform: "rotate(0deg)",
    direction: "rtl",
    "&.Mui-selected": {
      backgroundColor: "transparent",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "transparent",
    },
    "&.Mui-selected.Mui-focusVisible": {
      backgroundColor: "transparent",
    },
    "&:hover, &.Mui-selected:hover, &.Mui-focusVisible:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  menuItemContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    width: "100%",
    height: "100%",
    gap: 10,
    opacity: 1,
    transform: "rotate(0deg)",
  },
  checkbox: {
    padding: 0,
    color: theme.palette.primary.main,
    "&.Mui-checked": {
      color: theme.palette.primary.main,
    },
  },
  fieldSelect: {
    height: "auto",
    minHeight: "unset",
    width: "fit-content",
    maxWidth: "100%",
    borderRadius: "4px",
    overflow: "visible",
    backgroundColor: "transparent !important",
    color: theme.palette.secondary.main,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
      borderWidth: "1px",
      borderRadius: "4px",
    },
    "&:hover": {
      backgroundColor: "transparent !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "transparent",
    },
    "&.Mui-focused": {
      backgroundColor: `${theme.palette.common.white} !important`,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.secondary.main,
      borderWidth: "1px",
    },
    "&.Mui-focused:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.secondary.main,
    },
    "& .MuiSelect-icon": {
      display: "none",
    },
    "& .MuiSelect-select": {
      height: "auto !important",
      minHeight: "unset !important",
      padding: "0 !important",
    },
    "&.Mui-focused .MuiSelect-select": {
      padding: "4px 8px !important",
    },
  },
  fieldMenu: {
    width: "max-content",
    minWidth: ({ menuWidth }) => `${menuWidth}px !important`,
    maxWidth: "none",
    opacity: 1,
    transform: "rotate(0deg)",
    paddingTop: 4,
    paddingBottom: 4,
    boxSizing: "border-box",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.divider,
    borderRadius: 4,
    boxShadow: "0 4px 16px rgba(37, 56, 88, 0.12)",
    overflow: "hidden",
    maxHeight: ({ menuMaxHeight }) =>
      menuMaxHeight ? `${menuMaxHeight}px` : "none",
  },
  fieldMenuList: {
    paddingTop: 0,
    paddingBottom: 0,
    overflowY: "auto",
    maxHeight: ({ menuMaxHeight }) =>
      menuMaxHeight ? `${menuMaxHeight - 8}px` : "none",
  },
  fieldSelectValue: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    width: "100%",
    direction: "rtl",
  },
  fieldSelectText: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "140%",
    color: theme.palette.secondary.main,
    whiteSpace: "nowrap",
    textAlign: "start",
  },
  fieldSelectChevron: {
    fontSize: "18px !important",
    color: theme.palette.text.primary,
    flexShrink: 0,
  },
  fieldMenuItemContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "100%",
    gap: 16,
  },
  optionLabel: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "140%",
    color: theme.palette.secondary.main,
    whiteSpace: "nowrap",
  },
  optionSelected: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "140%",
    color: theme.palette.secondary.main,
    whiteSpace: "nowrap",
  },
  checkIcon: {
    fontSize: "18px !important",
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  plainOption: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
  },
  optionChip: {
    height: 26,
    minHeight: 26,
    borderRadius: 4,
    gap: 10,
    opacity: 1,
    transform: "rotate(0deg)",
    paddingTop: 8,
    paddingRight: 14,
    paddingBottom: 8,
    paddingLeft: 14,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
    color: theme.palette.common.white,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1,
    fontFamily: theme.typography.fontFamily,
    whiteSpace: "nowrap",
  },
}));
